import { useCallback, useEffect, useRef, useState } from 'react'

export type ResourceStatus = 'idle' | 'loading' | 'ready' | 'error'

export type LazyResource<T> = {
  status: ResourceStatus
  data: T | null
  retry: () => void
}

/**
 * One public read with {status, data, retry}, fetched once when `enabled` flips
 * true. Eager home feeds pass enabled=true (fire on mount); below-fold feeds
 * pass a near-viewport flag so the request defers until the section approaches
 * the viewport. A run-id guards against a stale resolve overwriting a newer one
 * (and against a re-render double-fetch); retry re-runs regardless of state.
 * Read-only — no mutations, no cache invalidation.
 */
export function useLazyResource<T>(fetcher: () => Promise<T>, enabled: boolean): LazyResource<T> {
  const [status, setStatus] = useState<ResourceStatus>('idle')
  const [data, setData] = useState<T | null>(null)
  const fetcherRef = useRef(fetcher)
  useEffect(() => {
    fetcherRef.current = fetcher
  }, [fetcher])
  const runIdRef = useRef(0)

  const run = useCallback(() => {
    const runId = ++runIdRef.current
    setStatus('loading')
    fetcherRef.current().then(
      (result) => {
        if (runId !== runIdRef.current) return
        setData(result)
        setStatus('ready')
      },
      () => {
        if (runId !== runIdRef.current) return
        setData(null)
        setStatus('error')
      },
    )
  }, [])

  useEffect(() => {
    if (!enabled || status !== 'idle') return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    run()
  }, [enabled, status, run])

  const retry = useCallback(() => run(), [run])

  return { status, data, retry }
}
